import { ISignupFormProps, SignUpRequest } from "./SignupFormProps";
import {
  Container,
  Disclaimer,
  FormText,
  TrackErrorMessage,
  TravelPassInfo,
} from "./SignupFormHelpers";
import React, { useState, useEffect } from "react";
import { useStaticQuery, graphql, navigate, Link, Slice } from "gatsby";
import { useAppSelector } from "../../../hooks";
import { useForm } from "react-hook-form";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import SuccessModal from "../SuccessModal/SuccessModal";
import PrivacyModal from "../PrivacyModal/PrivacyModal";
import SubTitle from "../../global/SubTitle/SubTitle";
import Title4 from "../../global/Title4/Title4";
import { getWifiDetails } from "../../../utils/auth0Client";
import { guestServiceProvider } from "../../../services/ServiceProvider";
import ErrorMessageAlert from "../ErrorMessageAlert/ErrorMessageAlert";
import { Constants } from "../../../@types/Constants";
import {
  addPageTypeInGTMDataLayer,
  travelPassEventsGTMDataLayer,
} from "../../../utils/datalayers";
import { getCRSSubSource } from "../../../utils/helpers";
import { alertDanger, linkStyle } from "./SignupForm.module.scss";
import queryString from "query-string";
import { TermsOfServiceModalSliceProvider } from "../../../context/SliceContexts/TermsOfServiceModalContext";

const CONFIRMATION_MESSAGE =
  "You have successfully signed up for Sonesta Travel Pass!  Please check your email to activate your account.";

const SignupForm: React.FC<ISignupFormProps> = (props) => {
  const { register, handleSubmit, errors, reset } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
  });
  const member = useAppSelector((state) => state.member);
  const required = "This field is required.";
  const [firstname, setFirstname] = useState("");
  const [validated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<JSX.Element | string | null>(
    null
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleSuccessClose = () => {
    setShowSuccess(false);
    props.onSignUp && props.onSignUp();
  };

  const handlePrivacyModalClose = () => {
    setShowPrivacyModal(false);
  };

  const handlePrivacyModalShow = () => {
    setShowPrivacyModal(true);
  };

  const handleTermsModalClose = () => {
    setShowTermsModal(false);
  };

  const handleTermsModalShow = () => {
    setShowTermsModal(true);
  };

  useEffect(() => {
    (async () => {
      if (member.isLoggedIn) {
        navigate("/account", { state: { isAuthenticated: true } });
      }
    })();
  }, [member.isLoggedIn]);

  useEffect(() => {
    addPageTypeInGTMDataLayer("sign up");
  }, []);

  const hotelDataQuery = useStaticQuery(graphql`
    {
      allHotel {
        nodes {
          crs_code
          name
        }
      }
    }
  `);

  const hotelData = hotelDataQuery?.allHotel?.nodes?.filter((property) => {
    return property?.crs_code;
  });

  const onSubmit = async (data, event) => {
    const [_lspUrl, synxisHotelId, hotelName] = getWifiDetails(
      hotelData,
      window.location
    );
    const initialValues = {
      synxisHotelId,
      hotelName,
    };
    const request = { ...initialValues, ...data };
    event.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);
    let response: any;
    try {
      const guestService = await guestServiceProvider();
      if (_lspUrl && synxisHotelId && hotelName) {
        //Wifi Enrollment Logic
        response = await guestService.signUpWifi(
          request.email,
          request.firstname,
          request.lastname,
          request.hotelName
        );
      } else {
        const queryParams =
          props.location?.search && props.location.search.length
            ? queryString.parse(props.location.search.toString())
            : null;

        const partner = queryParams?.partner;
        const enrollmentSource = queryParams?.source;

        const signUpRequest: SignUpRequest = {
          email: request.email,
          firstname: request.firstname,
          lastname: request.lastname,
        };

        if (partner && enrollmentSource) {
          signUpRequest.enrollment = {
            partner: partner,
            enrollmentSource: enrollmentSource,
          };
        }

        response = await guestService.signUp(signUpRequest);
      }
      if (response.insertOrUpdateUserProfile.statusCode === "500") {
        setErrorMessage(
          <>
            That email address is already enrolled. Try a different one or{" "}
            <Link to={"/login"} className={linkStyle}>
              Sign In
            </Link>
            .
          </>
        );
      } else {
        setShowSuccess(true);
        setFirstname(response?.name || request.firstname);
        reset();
        if (props.isTravelPass) {
          travelPassEventsGTMDataLayer(
            "Started - Email Sent",
            getCRSSubSource()
          );
        }
      }
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.error || error?.message || "Error Occurred"
      );
      reset();
    }
    setIsSubmitting(false);
  };

  return (
    <Container>
      <Row>
        <Col lg={4}>
          <SubTitle id={"create-travel-pass-account-text"}>
            Create Account
          </SubTitle>
          <TravelPassInfo>
            <div dangerouslySetInnerHTML={{ __html: props.body }} />
          </TravelPassInfo>
          <div className="mt-3 mb-3 font-weight-bold">
            <Button
              aria-label={`Learn More for Create Account`}
              className="text-uppercase w-100 learn-more-btn"
              onClick={() => navigate("/travel-pass")}
            >
              Learn More
            </Button>
          </div>
        </Col>
        <Col lg={{ span: 7, offset: 1 }}>
          {errorMessage && (
            <ErrorMessageAlert
              errorType={Constants.ERRORSTYPE.TRAVELPASS}
              errorSubType={Constants.ERRORSSUBTYPE.TRAVELPASS.SIGNUP}
              message={errorMessage}
            >
              <Alert variant={`danger`} className={alertDanger}>
                {errorMessage}
              </Alert>
            </ErrorMessageAlert>
          )}
          <Form
            noValidate
            validated={validated}
            onSubmit={handleSubmit(onSubmit)}
            id={`submit-enrollment-form`}
          >
            <Row>
              <Form.Group as={Col} lg={6} controlId="firstname">
                <FormText>
                  <Form.Label>First Name*</Form.Label>
                </FormText>
                <Form.Control
                  type="text"
                  name="firstname"
                  ref={register({
                    required: required,
                  })}
                  isInvalid={errors.firstname}
                  aria-invalid={errors.firstname ? "true" : "false"}
                  autoComplete="given-name"
                />
                <Form.Control.Feedback type="invalid">
                  <TrackErrorMessage
                    message={errors.firstname && errors.firstname.message}
                    field="firstname"
                  />
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} lg={6} controlId="lastname">
                <FormText>
                  {" "}
                  <Form.Label>Last Name*</Form.Label>
                </FormText>
                <Form.Control
                  type="text"
                  name="lastname"
                  ref={register({
                    required: required,
                  })}
                  isInvalid={errors.lastname}
                  aria-invalid={errors.lastname ? "true" : "false"}
                  autoComplete="family-name"
                />
                <Form.Control.Feedback type="invalid">
                  <TrackErrorMessage
                    message={errors.lastname && errors.lastname.message}
                    field="lastname"
                  />
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} lg={6} controlId="email">
                <FormText>
                  <Form.Label>Email Address*</Form.Label>
                </FormText>
                <Form.Control
                  type="email"
                  name="email"
                  ref={register({
                    required: required,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                      message: "A valid Email address is required.",
                    },
                  })}
                  isInvalid={errors.email}
                  aria-invalid={errors.email ? "true" : "false"}
                  autoComplete="email"
                />
                <Form.Control.Feedback type="invalid">
                  <TrackErrorMessage
                    message={errors.email && errors.email.message}
                    field="email"
                  />
                </Form.Control.Feedback>
              </Form.Group>
              {/* <Form.Group as={Col} lg={6} controlId="promoCode">
                <FormText><Form.Label>Promo Code</Form.Label></FormText>
                <Form.Control
                  type="text"
                  name="rlhc_promotionalsignupcode"
                  ref={register}
                  isInvalid={errors.rlhc_promotionalsignupcode}
                  aria-invalid={errors.rlhc_promotionalsignupcode ? "true" : "false"}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.rlhc_promotionalsignupcode && errors.rlhc_promotionalsignupcode.message}
                </Form.Control.Feedback>
              </Form.Group> */}
            </Row>
            <Button
              id={`submit-enrollment-form-button`}
              type="submit"
              className="text-uppercase w-100 mt-4"
              disabled={isSubmitting}
            >
              {isSubmitting ? `Please wait...` : `Create Account`}
            </Button>
            <Disclaimer className="mt-3 d-block">
              By submitting this form I certify that I have read, understand,
              and accept the{" "}
              <Button
                variant="link"
                className="termsOfService"
                onClick={handleTermsModalShow}
                tabIndex={0}
                id={"terms-of-servive-in-travel-pass"}
              >
                Terms of Service
              </Button>
              &nbsp;and&nbsp;
              <Button
                variant="link"
                className="termsOfService"
                onClick={handlePrivacyModalShow}
                tabIndex={0}
                id={"privacy-statement-in-travel-pass"}
              >
                Privacy Statement
              </Button>
              . <br></br> <br></br>
            </Disclaimer>
          </Form>
        </Col>
      </Row>
      <PrivacyModal show={showPrivacyModal} onHide={handlePrivacyModalClose} />
      <TermsOfServiceModalSliceProvider
        value={{ show: showTermsModal, onHide: handleTermsModalClose }}
      >
        <Slice alias="terms-of-service-modal" />
      </TermsOfServiceModalSliceProvider>
      <SuccessModal
        show={showSuccess}
        onHide={handleSuccessClose}
        modalSize={"lg"}
      >
        <SubTitle
          id={"account-creation-confirmation-header"}
          className="text-center mb-1"
        >
          CONFIRMATION
        </SubTitle>
        <Title4 id={"travel-pass-welcome-text"} className="m-3 text-capitalize">
          Welcome to Travel Pass {firstname}!{" "}
        </Title4>
        <p id="account-creation-confirmation-message" className="p-2">
          {CONFIRMATION_MESSAGE}
        </p>
        {/* <p className="m-0"><i className="font-weight-bold">You will receive a confirmation email shortly to finish your profile.</i></p> */}
      </SuccessModal>
    </Container>
  );
};

export default SignupForm;
